import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, CircularProgress,
} from '@mui/material';
import DatosMaestrosService from '../../services/datosMaestrosService';

/**
 * Alta al vuelo de un régimen diferencial desde el desplegable del trámite de Jubilación.
 * Si ya existe uno con ese nombre, el backend devuelve el existente y se usa ese.
 * Devuelve el régimen por `onCreado`.
 *
 * Es un Dialog de MUI (no un Modal de Bootstrap) porque en Recepción el formulario de inicio
 * vive dentro de otro Dialog de MUI: un modal de Bootstrap quedaba debajo de ese Dialog y su
 * trampa de foco no dejaba escribir. Entre Dialogs de MUI el apilado y el bloqueo de scroll los
 * maneja el mismo ModalManager.
 */
export default function ModalNuevoRegimenJubilacion({ show, onHide, onCreado }) {
    const [nombre, setNombre] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            setNombre('');
            setError('');
        }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation(); // el diálogo se abre desde dentro del <form> del trámite
        if (!nombre.trim()) {
            setError('Ingrese el nombre del régimen.');
            return;
        }
        setGuardando(true);
        setError('');
        try {
            const res = await DatosMaestrosService.createRegimenJubilacion({ nombre: nombre.trim() });
            onCreado(res.data);
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudo crear el régimen.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog
            open={show}
            onClose={guardando ? undefined : onHide}
            maxWidth="sm"
            fullWidth
            className="dialog-centrado-contenido"
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle sx={{ fontWeight: 600 }}>Nuevo régimen diferencial</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                        margin="dense"
                        slotProps={{ htmlInput: { maxLength: 150 } }}
                        placeholder="Ej: Trabajadores de la industria del vidrio"
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Nace con la documentación genérica de régimen diferencial. La documentación
                        específica se le agrega desde Datos Maestros → Regímenes de Jubilación.
                    </Alert>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onHide} disabled={guardando} color="inherit">Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={guardando}>
                        {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Crear régimen'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
